import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Truck, Check, PenLine, Clock, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SignaturePad from "@/components/SignaturePad";
import { DeleteButton } from "@/components/DeleteButton";
import { useRef } from "react";

export default function Deliveries() {
  const [requests, setRequests] = useState([]);
  const [signTarget, setSignTarget] = useState(null);
  const [glpMed, setGlpMed] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const EMPTY_LOG = { name: "", date_of_birth: "", phone: "", email: "", address: "", rx_numbers: "" };
  const [logForm, setLogForm] = useState(EMPTY_LOG);
  const sigRef = useRef(null);

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/delivery/requests"); setRequests(data); } catch { /* */ } }

  // group by patient (email when present, otherwise by name + DOB for admin logs without an account)
  const groups = {};
  requests.forEach((r) => {
    const key = r.email || r.user_id || `${(r.name || "").toLowerCase()}|${r.date_of_birth || ""}`;
    (groups[key] = groups[key] || { key, name: r.name, email: r.email, phone: r.phone, dob: r.date_of_birth, items: [] }).items.push(r);
  });
  const patients = Object.values(groups);

  async function submitLog(e) {
    e.preventDefault();
    if (!logForm.name || !logForm.address || !logForm.date_of_birth || !logForm.rx_numbers)
      return toast.error("Name, date of birth, address and Rx numbers are required.");
    try {
      const { data } = await api.post("/admin/delivery-logs", logForm);
      toast.success(data.linked
        ? "Delivery log saved and linked to the patient's account."
        : "Delivery log saved. It will auto-link once a patient signs up with matching name, DOB and address.");
      setLogForm(EMPTY_LOG); setLogOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function quickDeliver(req) {    try {
      await api.patch(`/delivery/requests/${req.id}/deliver`, {});
      toast.success("Marked as delivered.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  async function del(req) {
    try { await api.delete(`/delivery/requests/${req.id}`); toast.success("Request deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  async function confirmDeliver() {
    try {
      const body = {};
      if (sigRef.current && !sigRef.current.isEmpty()) body.signature = sigRef.current.toDataURL();
      if (glpMed.trim()) body.glp_medication = glpMed.trim();
      await api.patch(`/delivery/requests/${signTarget.id}/deliver`, body);
      toast.success(glpMed.trim() ? "Delivered — added to weekly GLP reorder reminder." : "Marked as delivered.");
      setSignTarget(null); setGlpMed(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Delivery Requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">Grouped by patient. Mark deliveries complete and capture a signature on mobile.</p>
        </div>
        <Button className="rounded-full" onClick={() => { setLogForm(EMPTY_LOG); setLogOpen(true); }} data-testid="delivery-add-log">
          <Plus className="mr-1 h-4 w-4" /> Add Delivery Log
        </Button>
      </div>

      {patients.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">No delivery requests yet.</div>
      ) : (
        <Accordion type="multiple" className="mt-6 space-y-3">
          {patients.map((p) => {
            const pending = p.items.filter((i) => !i.delivered).length;
            return (
              <AccordionItem key={p.key} value={p.key} className="rounded-xl border border-border/60 bg-card px-4" data-testid={`delivery-patient-${p.key}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex flex-1 items-center justify-between pr-3 text-left">
                    <div>
                      <p className="font-heading font-bold">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email || (p.dob ? `DOB ${p.dob}` : "No account on file")} {p.phone ? `• ${p.phone}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {pending > 0 && <Badge className="bg-orange-500">{pending} pending</Badge>}
                      <Badge variant="outline">{p.items.length} total</Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pb-4">
                  {p.items.map((r) => (
                    <div key={r.id} className="rounded-lg border border-border/60 bg-background p-4" data-testid={`delivery-request-${r.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          {r.created_by_admin && (
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="border-primary/40 text-primary">Admin log</Badge>
                              <Badge className={r.user_id ? "bg-primary" : "bg-orange-500"}>{r.user_id ? "Linked to account" : "Awaiting patient signup"}</Badge>
                            </div>
                          )}
                          <p className="text-sm"><span className="font-medium">Rx:</span> {r.rx_numbers}</p>
                          <p className="text-sm text-muted-foreground">{r.address}</p>
                          {r.date_of_birth && <p className="text-xs text-muted-foreground">DOB: {r.date_of_birth}</p>}
                          {r.insurance && (
                            <p className="mt-1 text-xs text-muted-foreground" data-testid={`delivery-insurance-${r.id}`}>
                              <span className="font-medium text-foreground">Insurance:</span> {r.insurance.insurance_name} · ID {r.insurance.insurance_id} · BIN {r.insurance.bin_rx} · PCN {r.insurance.pcn} · GRP {r.insurance.grp_rx}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-muted-foreground">Requested {format(new Date(r.created_at), "MMM d, yyyy h:mm a")}</p>
                          {r.delivered && (
                            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-primary"><Check className="h-3 w-3" /> Delivered {format(new Date(r.delivered_at), "MMM d, yyyy h:mm a")}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {r.delivered ? (
                            <Badge className="bg-primary">Delivered</Badge>
                          ) : (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="rounded-full" onClick={() => quickDeliver(r)} data-testid={`delivery-mark-${r.id}`}>
                                <Clock className="mr-1 h-3 w-3" /> Mark Delivered
                              </Button>
                              <Button size="sm" className="rounded-full" onClick={() => { setGlpMed(""); setSignTarget(r); }} data-testid={`delivery-sign-${r.id}`}>
                                <PenLine className="mr-1 h-3 w-3" /> Sign / GLP
                              </Button>
                            </div>
                          )}
                          {r.signature && <img src={r.signature} alt="signature" className="h-14 rounded border border-border bg-white" />}
                          <DeleteButton onConfirm={() => del(r)} label="this delivery request" testId={`delivery-delete-${r.id}`} variant="text" />
                        </div>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <Dialog open={!!signTarget} onOpenChange={(o) => { if (!o) { setSignTarget(null); setGlpMed(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Confirm Delivery — {signTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Ask the patient to sign below to confirm receipt (optional). Works with touch screens.</p>
          <div className="rounded-lg border border-border bg-white p-3">
            <SignaturePad ref={sigRef} label="Patient signature" testId="delivery-signature" />
          </div>
          <div className="space-y-2 rounded-lg border border-primary/30 bg-secondary/40 p-3">
            <Label htmlFor="glp" className="text-sm font-medium">GLP / Insulin medication (optional)</Label>
            <Input id="glp" data-testid="delivery-glp-medication" placeholder="e.g. Ozempic, Mounjaro, insulin" value={glpMed} onChange={(e) => setGlpMed(e.target.value)} />
            <p className="text-xs text-muted-foreground">If this refill is a GLP/insulin, enter it to add it to the weekly reorder reminder email.</p>
          </div>
          <Button className="rounded-full" onClick={confirmDeliver} data-testid="delivery-confirm-sign">
            <Check className="mr-1 h-4 w-4" /> Mark Delivered
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Delivery Log</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Log a delivery even before the patient has an account. We link it automatically by matching name, date of birth and address.</p>
          <form onSubmit={submitLog} className="space-y-4">
            <div className="space-y-2"><Label>Full name</Label><Input data-testid="log-name" value={logForm.name} onChange={(e) => setLogForm({ ...logForm, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date of birth</Label><Input type="date" data-testid="log-dob" value={logForm.date_of_birth} onChange={(e) => setLogForm({ ...logForm, date_of_birth: e.target.value })} required /></div>
              <div className="space-y-2"><Label>Phone (optional)</Label><Input data-testid="log-phone" value={logForm.phone} onChange={(e) => setLogForm({ ...logForm, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Email (optional)</Label><Input type="email" data-testid="log-email" value={logForm.email} onChange={(e) => setLogForm({ ...logForm, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Delivery address</Label><Input data-testid="log-address" value={logForm.address} onChange={(e) => setLogForm({ ...logForm, address: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Rx numbers</Label><Input data-testid="log-rx" placeholder="e.g. 1234567, 8910111" value={logForm.rx_numbers} onChange={(e) => setLogForm({ ...logForm, rx_numbers: e.target.value })} required /></div>
            <Button type="submit" className="w-full rounded-full" data-testid="log-submit"><Plus className="mr-1 h-4 w-4" /> Save Delivery Log</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
